import { addDays, addMonths, rangeForView, startOfDay } from '@crm/shared';
import { Button, Card, CardContent, cn, Select, Spinner } from '@crm/ui';
import { ChevronLeft, ChevronRight, ClipboardCheck, SlidersHorizontal } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLayoutStore, useOpenSection } from '@/app/layouts';
import { Autocomplete, type AutocompleteOption, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan, useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { SCHEDULE_ROUTES, useAgenda } from '../api';
import { type AgendaFilters, AgendaSidebar } from '../components/agenda-sidebar';
import { AppointmentDialog } from '../components/appointment-dialog';
import { VIEW_SURFACE } from '../components/calendar-colors';
import { CreateMenu } from '../components/create-menu';
import { MonthGrid } from '../components/month-grid';
import { OperationTabs } from '../components/operation-tabs';
import { ScheduleList } from '../components/schedule-list';
import { TimeGrid } from '../components/time-grid';
import { longDayLabel, shortDayLabel } from '../date-labels';

type View = 'day' | 'week' | 'month' | 'schedule';

const VIEW_LABELS: Record<View, string> = {
  day: 'Dia',
  week: 'Semana',
  month: 'Mes',
  schedule: 'Programacao',
};

/** As unicas visoes oferecidas no celular. */
const MOBILE_VIEWS: View[] = ['day', 'schedule'];

const monthLabel = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });
const shortDate = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'short' });
/** Na programacao o periodo e longo: dd/mm cabe e nao vira abreviacao. */
const numericDate = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

export function AgendaPage() {
  useDocumentTitle('Agenda');

  const canWrite = useCan('schedule', 'WRITE');
  const { closeAfterNavigate } = useOpenSection();
  const { user } = useSession();
  const units = user?.units ?? [];

  // A agenda abre no dia: e o que se olha o tempo todo na operacao.
  const [view, setView] = useState<View>('day');
  const [reference, setReference] = useState(() => startOfDay(new Date()));
  const [selectedUnits, setSelectedUnits] = useState<AutocompleteOption[]>([]);
  const unitIds = selectedUnits.map((unit) => unit.id);
  const [filters, setFilters] = useState<AgendaFilters>({
    professionalIds: [],
    customerIds: [],
  });
  const setFullWidth = useLayoutStore((state) => state.setFullWidth);

  /** No celular os filtros ficam atras do botao Filtrar. */
  const [showFilters, setShowFilters] = useState(false);

  /** Fechado, novo (agendamento ou link) ou editando - sem string vazia como sentinela. */
  const [dialog, setDialog] = useState<
    | { mode: 'new'; intent: 'appointment' | 'link'; startAt?: Date }
    | { mode: 'edit'; id: string }
    | null
  >(null);

  // A agenda toma a largura toda da tela; o menu continua o mesmo das outras
  // paginas, so um pouco mais estreito para sobrar espaco para a grade.
  useEffect(() => {
    setFullWidth(true);

    return () => setFullWidth(false);
  }, [setFullWidth]);

  const range = rangeForView(view, reference);
  // Uma unidade so vai no filtro da API; varias sao filtradas aqui, ja que o
  // backend restringe a consulta as unidades que o usuario atende.
  const agenda = useAgenda({
    from: range.from,
    to: range.to,
    unitId: unitIds.length === 1 ? unitIds[0] : undefined,
  });
  const loaded = agenda.data?.data ?? [];

  // Marcar ninguem = ver todo mundo, como as agendas do Google.
  const appointments = loaded.filter(
    (item) =>
      (unitIds.length < 2 || unitIds.includes(item.unitId)) &&
      (filters.professionalIds.length === 0 ||
        (item.professionalId !== null && filters.professionalIds.includes(item.professionalId))) &&
      (filters.customerIds.length === 0 || filters.customerIds.includes(item.customerId)),
  );

  /** Uma seta anda o tamanho da visao, como no Google Agenda. */
  function move(direction: 1 | -1) {
    setReference((current) => {
      if (view === 'day') return addDays(current, direction);
      if (view === 'week') return addDays(current, 7 * direction);
      if (view === 'schedule') return addDays(current, 30 * direction);
      return addMonths(current, direction);
    });
  }

  const period = `${numericDate.format(range.from)} a ${numericDate.format(range.to)}`;

  const title =
    view === 'month'
      ? monthLabel.format(reference)
      : view === 'day'
        ? longDayLabel(reference)
        : view === 'schedule'
          ? period
          : `${shortDate.format(range.from)} - ${shortDate.format(range.to)}`;

  /** Entre as setas, no celular, o ano nao cabe - e nem faz falta. */
  const shortTitle =
    view === 'month'
      ? monthLabel.format(reference)
      : view === 'day'
        ? shortDayLabel(reference)
        : view === 'schedule'
          ? period
          : `${shortDate.format(range.from)} - ${shortDate.format(range.to)}`;

  const weekDays = Array.from({ length: 7 }, (_, index) => addDays(range.from, index));

  return (
    <div className="space-y-4">
      <OperationTabs current="agenda" />

      {/* No celular as abas ja dizem em que tela se esta. */}
      <div className="hidden sm:block">
        <PageHeader
          title="Agenda"
          actions={
            // O caminho de volta para a operacao do dia: a mesma agenda, vista
            // pelo andamento de cada atendimento.
            <div className="flex items-center gap-2">
              <Button asChild className="hidden sm:inline-flex">
                {/* Fecha o submenu junto: a nova tela ja diz onde se esta. */}
                <Link
                  to={SCHEDULE_ROUTES.SERVICE_DESK}
                  onClick={() => closeAfterNavigate(SCHEDULE_ROUTES.SERVICE_DESK)}
                >
                  <ClipboardCheck className="h-4 w-4" />
                  Ver atendimentos
                </Link>
              </Button>
            </div>
          }
        />
      </div>

      {/* A visao vem primeiro: e ela que diz o que as setas ao lado de
          "Hoje" andam - um dia ou um mes de programacao. */}
      <div className="mb-3 lg:hidden">
        {/* Semana e mes nao cabem num celular: sobram as duas visoes que
            funcionam na mao, lado a lado, sem menu para abrir. */}
        <div
          role="tablist"
          aria-label="Visao da agenda"
          // No tablet a faixa para de atravessar a tela: duas abas de 384px
          // cada nao sao um seletor, sao um cabecalho.
          className="flex items-center gap-1 rounded-full bg-muted p-1 sm:max-w-sm"
        >
          {MOBILE_VIEWS.map((option) => {
            const active = view === option;

            return (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setView(option)}
                className={cn(
                  'h-11 flex-1 rounded-full text-base transition-colors',
                  active
                    ? 'bg-background font-medium text-foreground shadow-sm'
                    : 'text-muted-foreground',
                )}
              >
                {VIEW_LABELS[option]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Barra no formato do Google Agenda: hoje + setas + periodo, e a
          escolha da visao do outro lado. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="h-11 shrink-0 rounded-full border-border px-5 text-base shadow-none"
            onClick={() => setReference(startOfDay(new Date()))}
          >
            Hoje
          </Button>

          {/* O periodo fica entre as setas: elas mudam o que esta escrito ali. */}
          <div className="flex min-w-0 flex-1 items-center gap-1 lg:flex-none">
            <Button
              variant="ghost"
              size="icon"
              className="h-14 w-14 shrink-0 [&_svg]:size-8"
              aria-label="Anterior"
              onClick={() => move(-1)}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>

            <p className="min-w-0 flex-1 truncate text-center text-lg font-medium first-letter:uppercase sm:text-xl lg:flex-none lg:px-2 lg:text-left lg:text-2xl">
              <span className="sm:hidden">{shortTitle}</span>
              <span className="hidden sm:inline">{title}</span>
            </p>

            <Button
              variant="ghost"
              size="icon"
              className="h-14 w-14 shrink-0 [&_svg]:size-8"
              aria-label="Proximo"
              onClick={() => move(1)}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>
        </div>

        <div className="hidden shrink-0 items-center gap-2 lg:flex">
          <Select
            className="h-11 w-40 rounded-full border-border bg-background pr-8 text-base shadow-none"
            aria-label="Visao da agenda"
            value={view}
            onChange={(event) => setView(event.target.value as View)}
          >
            {(Object.keys(VIEW_LABELS) as View[]).map((option) => (
              <option key={option} value={option}>
                {VIEW_LABELS[option]}
              </option>
            ))}
          </Select>

          {/* Na agenda a unidade vem depois da visao, para nao competir com
              a navegacao de datas. */}
          {units.length > 1 ? (
            <Autocomplete
              className="w-52"
              size="lg"
              inputClassName="rounded-full border-border bg-background shadow-none"
              multiple
              emptyLabel="Todas as unidades"
              placeholder="Filtrar unidade"
              url="/api/v1/units"
              value={selectedUnits}
              onChange={setSelectedUnits}
            />
          ) : null}
        </div>
      </div>

      {agenda.isError ? (
        <p className="text-sm text-destructive">{toErrorMessage(agenda.error)}</p>
      ) : null}

      {/* No celular as duas acoes da agenda dividem a linha: criar o horario
          e mostrar ou esconder os filtros de profissional e cliente. Do tablet
          em diante elas voltam ao tamanho do proprio texto - esticadas por
          384px cada, viravam duas faixas. */}
      <div className="flex gap-2 lg:hidden">
        {canWrite ? (
          <CreateMenu
            // Mesma altura do Filtrar ao lado - eles dividem a linha.
            className="flex-1 sm:flex-none [&>button]:h-11"
            onNewAppointment={() => setDialog({ mode: 'new', intent: 'appointment' })}
            onNewLink={() => setDialog({ mode: 'new', intent: 'link' })}
          />
        ) : null}

        <Button
          variant="outline"
          className="h-11 flex-1 rounded-full sm:flex-none sm:px-6"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((open) => !open)}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filtrar
        </Button>
      </div>

      {/* No desktop a coluna do calendario respira: mais folga ate a grade e
          o calendario um pouco acima, na altura do primeiro horario. */}
      <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-5">
        <aside
          className={cn(
            // Sem recuo proprio: a folga ate a grade e so o gap da grade,
            // senao a coluna fica com mais ar de um lado que do outro.
            'lg:sticky lg:top-14 lg:block lg:self-start',
            showFilters ? 'block' : 'hidden',
          )}
        >
          {/* A unidade e filtro como os outros: no celular ela abre junto. */}
          {units.length > 1 ? (
            <div className="mb-3 lg:hidden">
              <Autocomplete
                size="lg"
                inputClassName="rounded-full border-border bg-background shadow-none"
                multiple
                emptyLabel="Todas as unidades"
                placeholder="Filtrar unidade"
                url="/api/v1/units"
                value={selectedUnits}
                onChange={setSelectedUnits}
              />
            </div>
          ) : null}

          <AgendaSidebar
            reference={reference}
            onSelectDay={(day) => setReference(day)}
            appointments={loaded}
            filters={filters}
            onChangeFilters={setFilters}
            canWrite={canWrite}
            onCreate={() => setDialog({ mode: 'new', intent: 'appointment' })}
            onCreateLink={() => setDialog({ mode: 'new', intent: 'link' })}
          />
        </aside>

        <div className="min-w-0">
          {agenda.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : view === 'schedule' ? (
            <ScheduleList
              appointments={appointments}
              onSelect={(id) => setDialog({ mode: 'edit', id })}
            />
          ) : view === 'month' ? (
            <MonthGrid
              reference={reference}
              appointments={appointments}
              onSelectDay={(day) => {
                setReference(day);
                setView('day');
              }}
              onSelect={(id) => setDialog({ mode: 'edit', id })}
            />
          ) : (
            // No celular a grade ocupa a tela toda: sem margem e sem cantos,
            // que so tiravam largura de quem tem menos.
            <Card
              className={cn(
                '-mx-3 rounded-none border-x-0 sm:mx-0 sm:rounded-xl sm:border-x',
                VIEW_SURFACE,
              )}
            >
              <CardContent className="p-0 sm:p-0">
                <TimeGrid
                  days={view === 'day' ? [reference] : weekDays}
                  appointments={appointments}
                  onSelect={(id) => setDialog({ mode: 'edit', id })}
                  onCreateAt={
                    canWrite
                      ? (startAt) => setDialog({ mode: 'new', intent: 'appointment', startAt })
                      : undefined
                  }
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Montada so quando aberta: cada abertura comeca do zero. */}
      {dialog !== null ? (
        <AppointmentDialog
          id={dialog.mode === 'edit' ? dialog.id : undefined}
          intent={dialog.mode === 'new' ? dialog.intent : 'appointment'}
          startAt={dialog.mode === 'new' ? dialog.startAt : undefined}
          // Uma unidade filtrada define a do agendamento; nenhuma ou varias,
          // vale a primeira que o usuario atende.
          unitId={unitIds.length === 1 ? unitIds[0] : units[0]?.id}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
